import {atomWithImmer} from 'jotai-immer';

import {store} from './global';
import {genSpeech} from "./audio";
import {setFocused} from "./active";
import {is_string, get_hms, time_string} from "../utils";

export interface TimerT {
  name: string;
  duration: number; // in ms
  end: number; // in epoch ms
  recipeName?: string;
}

export const timersAtom = atomWithImmer<Record<string, TimerT>>({});

export const createTimer = (name: string, duration: string | number, recipeName?: string) => {
  //TODO if existing timer with that name, modify it
  if (is_string(duration)) {
    duration = durationStringToMs(duration);
  }
  // anything less than a second is probably actually in seconds
  if (duration < 1000) {
    duration *= 1000;
  }
  store.set(timersAtom, (state) => {
    state[name] = {
      name,
      duration,
      end: new Date(new Date().getTime() + duration),
      recipeName,
    };
  });
  genSpeech({text: `Set timer ${name} for ${time_string(get_hms(duration / 1000), "human")}`})
}

// checks status of this timer
export const checkTimer = (name: string) => {
  const timer = store.get(timersAtom)[name];
  const now = new Date();
  const time_str = time_string(get_hms((timer.end - now) / 1000), "human");
  genSpeech({text: `Timer ${timer.name} has ${time_str} left`});
}

// deletes a timer from our list
export const deleteTimer = (name) => {
  store.set(timersAtom, (state) => {
    delete state[name];
  });
  //genSpeech({text: `Deleted timer ${name}`});
}

// finishes a timer and then calls deleteTimer()
export const finishTimer = (name) => {
  genSpeech({text: `Timer ${name} is ready`});
  const timer = store.get(timersAtom)[name];
  setFocused(timer.recipeName);
  deleteTimer(name);
}

// updates an existing timer, either adding time or setting it to a new value
export const updateTimer = (name, duration, isIncr=false) => {
  if (is_string(duration)) {
    duration = durationStringToMs(duration);
  }
  store.set(timersAtom, (state) => {
    // make sure timer exists
    if (!state[name]) {
      console.log(`Error: timer ${name} does not exist`);
      return;
    }
    if (isIncr) {
      state[name].duration += duration;
      state[name].end = new Date(state[name].end.getTime() + duration);
      genSpeech({text: `Added ${time_string(get_hms(duration / 1000), "human")} to timer ${name}`});
    } else {
      state[name].duration = duration;
      state[name].end = new Date(new Date().getTime() + duration);
      genSpeech({text: `Set timer ${name} to ${time_string(get_hms(duration / 1000), "human")}`});
    }
  });
}

export const renameTimer = (oldName, newName) => {
  store.set(timersAtom, (state) => {
    state[newName] = state[oldName];
    state[newName].name = newName;
    delete state[oldName];
  });
  genSpeech({text: `Renamed timer ${oldName} to ${newName}`});
}

// converts a duration string in format 'ptXY' to ms
const durationStringToMs = (duration: string) => {
  let factor = 1000;
  let c = duration.slice(-1).toLowerCase();
  switch (c) {
    case "s":
      factor = 1000;
      break;
    case "m":
      factor = 60000;
      break;
    default:
      throw new Error(`Don't know about duration element ${c}`);
  }
  const amount = parseFloat(duration.slice(2, -1));
  const ms = amount * factor;
  return ms;
};

// processes a successfully matched intent
export const processTimerIntent = (intent) => {
  console.log("Got timer intent", intent);
  const timers = store.get(timersAtom);

  const timerName = intent.slots.name.value.interpretedValue || "";
  switch (intent.name) {
    case 'check_all_timers':
      timers.forEach((name) => checkTimer(name));
      break;
    case "create_timer":
      createTimer(timerName, intent.slots.duration.value.interpretedValue);
      break;
    case "delete_timer":
      deleteTimer(timerName);
      break;
    case "check_timer":
      checkTimer(timerName);
      break;
    default:
      console.log("Unknown timer intent", intent);
      genSpeech({text: `Sorry, did not understand intent ${intent.name}`});
  }
}
